import React, { useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';

interface AISummaryProps {
  summary: string;
  clientId: string;
  onUpdate?: (newSummary: string) => void;
}

const AISummary: React.FC<AISummaryProps> = ({ summary, clientId, onUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const handleUpdateSummary = async () => {
    setIsUpdating(true);
    setError(null);
    setShowSuccess(false);

    try {
      const response = await apiService.updateAISummary(clientId);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000); // Hide success message after 3 seconds

      // If the response includes the new summary and we have an onUpdate callback, use it
      if (response.summary && onUpdate) {
        onUpdate(response.summary);
      }
    } catch (err) {
      console.error('Failed to update AI summary:', err);
      setError('Failed to update AI summary. Please try again.');
      setTimeout(() => setError(null), 5000); // Hide error message after 5 seconds
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div
        className='mb-16'
        style={{
          backgroundColor: 'rgba(250, 251, 253, 1)',
          borderRadius: '24px',
          padding: '26px'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AutoAwesomeIcon
              style={{
                fontSize: '22px',
                color: '#000000' // Black outline
              }}
            />
            <div style={{
              fontFamily: 'Satoshi, sans-serif',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              AI Generated Summary
            </div>
          </div>

          {user?.canEdit ? (
            <button
              onClick={handleUpdateSummary}
              disabled={isUpdating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isUpdating ? '#9ca3af' : '#007bff',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                fontFamily: 'Satoshi, sans-serif',
                transition: 'background-color 0.2s ease',
              }}
              title="Update AI Summary"
            >
              <RefreshIcon
                style={{
                  fontSize: '16px',
                  animation: isUpdating ? 'spin 1s linear infinite' : 'none'
                }}
              />
              {isUpdating ? 'Updating...' : 'Update'}
            </button>
          ) : null}
        </div>
        <div style={{
          fontFamily: 'Satoshi, sans-serif',
          fontSize: '18px',
          lineHeight: '1.6',
          color: '#374151'
        }}>
          {summary}
        </div>
      </div>

      {/* Loading Popup */}
      {isUpdating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <RefreshIcon
                style={{
                  fontSize: '32px',
                  color: '#007bff',
                  animation: 'spin 1s linear infinite'
                }}
              />
            </div>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1a1a1a',
              fontFamily: 'Satoshi, sans-serif'
            }}>
              Updating AI Summary
            </h3>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#6b7280',
              fontFamily: 'Satoshi, sans-serif'
            }}>
              Please wait while we regenerate the summary...
            </p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: 'Satoshi, sans-serif',
          zIndex: 1001,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          AI Summary updated successfully!
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: 'Satoshi, sans-serif',
          zIndex: 1001,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          {error}
        </div>
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default AISummary; 